import React, { FC, useEffect } from 'react';
import { ascComparator, T } from 'src/shared/helpers';
import styled from 'styled-components';

import { Checkbox } from '../../../../../../widgets/checkbox';
import { useDialogManager } from '../../../../../../widgets/dialog';
import { useProductConfigurationState } from 'src/components/pages/productConfiguration/state';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import Copy from 'src/shared/icons/copy-solid.svg';
import Edit from 'src/shared/icons/edit-solid.svg';
import Plus from 'src/shared/icons/plus-solid.svg';
import Trash from 'src/shared/icons/trash-alt-solid.svg';
import theme from 'src/shared/theme';
import { ComposerTemplateSubTreeElement, ComposerTemplateTreeElement } from '../../../configurator-composer/composer/components/composerTemplateTreeElement';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import ComposerSaveAreaDialog from '../../../configurator-composer/composer/dialogs/templates/composerSaveAreaDialog';
import { TreeNodeHook } from '../../scene-editors/hooks/useTreeNodeManager';
import { ToolGrid } from '../../toolsgrid';
import { ComposerAreaNode, ComposerTemplateNode } from '../attributes/composerAttributesModels';

const ComposerTemplatesContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    gap: 8px;
`;

const ComposerTemplatesHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: start;
    border-bottom: 1px solid ${theme.app.borderColor};
`;

const ComposerAreasHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: start;
    flex-grow: 1;
    border-bottom: 1px solid ${theme.app.borderColor};
`;

const ComposerTemplatesBody = styled.div`
    padding: 10px 0;
    flex: 1;
    overflow-y: auto;
`;

const ComposerTemplatesList = styled.div<{ $hasSelected: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
    margin: 0;
    height: 100%;
    overflow: hidden;
    ${(props) => props.$hasSelected && 'padding-top: 30px; height: calc(100% - 30px);'}
    overflow-y: auto;
`;

const ComposerTemplatesOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: transparent;
`;

const ComposerTemplatesDeselectorContainer = styled.div`
    position: absolute;
    height: 30px;
    top: 0;
    left: 0;
    z-index: 10;
    cursor: pointer;
`;
const ComposerTemplatesCollapseContainer = styled.div`
    position: absolute;
    height: 30px;
    top: 0;
    right: 0;
    font-size: 12px;
    text-align: right;
    z-index: 9;
    cursor: pointer;
`;

const ComposerTemplatesSelector: FC<{ selected: boolean; onClick: () => void }> = ({ selected, onClick }) => {
    return (
        <ComposerTemplatesDeselectorContainer onClick={onClick}>
            <Checkbox checked={selected} onChange={onClick} label='Select all items' />
        </ComposerTemplatesDeselectorContainer>
    );
};

const ComposerTemplatesCollapseSelector: FC<{ onExpand: () => void; onCollapse: () => void }> = ({ onExpand, onCollapse }) => {
    return (
        <ComposerTemplatesCollapseContainer>
            <span onClick={onExpand} style={{ marginRight: '10px' }}>
                {T._('Expand All', 'Admin')}
            </span>
            <span onClick={onCollapse}>{T._('Collapse All', 'Admin')}</span>
        </ComposerTemplatesCollapseContainer>
    );
};
const EmptyState = styled.div`
    padding: 20px;
    text-align: center;
    color: ${theme.app.textColor};
`;
const ComposerTemplatesEmptyState: FC<{ label: string }> = ({ label }) => {
    return (
        <ComposerTemplatesBody>
            <EmptyState>{label}</EmptyState>
        </ComposerTemplatesBody>
    );
};

const ComposerTemplates: FC<{
    templateState: TreeNodeHook<ComposerTemplateNode>;
    areaState: TreeNodeHook<ComposerAreaNode>;
    designState: TreeNodeHook<ComposerAreaNode>;
}> = ({ templateState, areaState, designState }) => {
    const [
        storeTemplate,
        storeArea,
        sceneMaterials,
        sceneObjects,
        sceneCameras,
        selectedTemplate,
        selectedAreas,
        storeCloneTemplate,
        storeUpdateTemplate,
        storeRemoveTemplate,
        storeCloneArea,
        storeUpdateArea,
        storeRemoveArea,
        selectTemplates,
        selectAreas
    ] = useComposerStore((state) => [
        state.templates,
        state.areas,
        state.sceneMaterialList,
        state.sceneObjectList,
        state.sceneCameraList,
        state.selectedTemplates,
        state.selectedAreas,
        state.cloneTemplate,
        state.updateTemplate,
        state.removeTemplate,
        state.cloneArea,
        state.updateArea,
        state.removeArea,
        state.selectTemplates,
        state.selectAreas
    ]);
    const [zkScene, sceneEditor, zkProduct] = usePreview3DStore((x) => [x.zkScene, x.sceneEditor, x.zkProduct]);

    // const preview3DStore = useContext(Preview3DStoreContext);
    const { state: productState } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const meshList = sceneObjects.filter((x) => sceneEditor!.isRealMesh(x)).sort(ascComparator((item) => item.name ?? '')) ?? [];
    const materialsList =
        sceneMaterials.filter((x) => x.id != 'default material' && x.name != 'default material').sort(ascComparator((item) => item.name ?? '')) ?? [];
    const camerasList = sceneCameras.filter((x) => x.id != 'default camera' && x.name != 'default camera').sort(ascComparator((item) => item.name ?? '')) ?? [];

    // console.log(meshList, materialsList, camerasList);
    const {
        selectedIds: treeTemplatesSelected,
        filteredData: treeTemplatesFiltered,
        addNode: treeTemplateAddNode,
        updateNode: treeTemplateUpdateNode,
        findNodeById: treeTemplateFindNodeById,
        removeNodes: treeTemplateRemoveNodes,
        setSelectedIds: treeTemplateSetSelectedIds
    } = templateState;
    const {
        selectedIds: treeAreasSelected,
        filteredData: treeAreasFiltered,
        addNode: treeAreaAddNode,
        updateNode: treeAreaUpdateNode,
        findNodeById: treeAreaFindNodeById,
        removeNodes: treeAreaRemoveNodes,
        setSelectedIds: treeAreaSetSelectedIds
    } = areaState;

    const {
        selectedIds: treeDesignsSelected,
        filteredData: treeDesignsFiltered,
        addNode: treeDesignsAddNode,
        updateNode: treeDesignsUpdateNode,
        findNodeById: treeDesignsFindNodeById,
        removeNodes: treeDesignsRemoveNodes,
        setSelectedIds: treeDesignsSetSelectedIds
    } = designState;
    // const selectedAttributes = React.useMemo(() => {
    // 	if (!selectedIds || selectedIds.length === 0) return null;
    // 	return selectedIds.map((id) => findNodeById(id)).filter((x) => x) as ComposerAttributeNode[];
    // }, [selectedIds, findNodeById]);
    const sceneMaterialList = sceneEditor?.getAllMaterials()?.filter((x) => x.name != 'default material' && x.id != 'default material') || [];

    const templateButtons = [
        { icon: <Plus />, name: T._('Add', 'Admin'), tooltip: T._('Add Template', 'Admin'), onClick: () => onTemplateButtonClick('add') },
        {
            icon: <Edit />,
            name: T._('Edit', 'Admin'),
            tooltip: T._('Edit Template', 'Admin'),
            disabled: treeTemplatesSelected.length !== 1,
            onClick: () => onTemplateButtonClick('edit')
        },
        {
            icon: <Trash />,
            name: T._('Remove', 'Admin'),
            tooltip: T._('Remove Template', 'Admin'),
            disabled: treeTemplatesSelected.length === 0,
            onClick: () => onTemplateButtonClick('remove')
        },
        {
            icon: <Copy />,
            name: T._('Duplicate', 'Admin'),
            tooltip: T._('Duplicate Template', 'Admin'),
            disabled: treeTemplatesSelected.length !== 1,
            onClick: () => onTemplateButtonClick('duplicate')
        }
    ];

    const areaButtons = [
        {
            icon: <Plus />,
            name: T._('Add', 'Admin'),
            tooltip: T._('Add Area', 'Admin'),
            onClick: () => onAreaButtonClick('add')
        },
        {
            icon: <Edit />,
            name: T._('Edit', 'Admin'),
            tooltip: T._('Edit Area', 'Admin'),
            disabled: treeAreasSelected.length !== 1,
            onClick: () => onAreaButtonClick('edit')
        },
        {
            icon: <Trash />,
            name: T._('Remove', 'Admin'),
            tooltip: T._('Remove Area', 'Admin'),
            disabled: treeAreasSelected.length === 0,
            onClick: () => onAreaButtonClick('remove')
        },
        {
            icon: <Copy />,
            name: T._('Duplicate', 'Admin'),
            tooltip: T._('Duplicate Area', 'Admin'),
            disabled: treeAreasSelected.length !== 1,
            onClick: () => onAreaButtonClick('duplicate')
        }
    ];

    const saveTemplateHandler = (attribute: ComposerTemplateNode) => {};

    const handleSaveArea = (area: ComposerAreaNode, isEdit: boolean) => {
        if (isEdit) {
            treeAreaUpdateNode(area.id, { ...area });
        } else {
            treeAreaAddNode(area);
        }
        storeUpdateArea(area);
    };

    const onTemplateButtonClick = (action: string) => {
        switch (action) {
            case 'add':
                break;
            case 'edit':
                break;
            case 'remove':
                const selectedTemplatesIDs = treeTemplatesFiltered.filter((x) => treeTemplatesSelected.includes(x.id)).map((x) => x.id);
                selectedTemplatesIDs.forEach((id) => {
                    storeRemoveTemplate(id);
                });
                treeTemplateRemoveNodes(selectedTemplatesIDs);
                break;
            case 'duplicate':
                const selectedTemplate = treeTemplatesFiltered.find((x) => treeTemplatesSelected.includes(x.id));
                if (selectedTemplate) {
                    const clonedTemplate = storeCloneTemplate(selectedTemplate);
                    treeTemplateAddNode(clonedTemplate);
                }
                break;
            default:
                console.warn('Unknown action:', action);
        }
    };

    const handleSaveAreaButton = (area: ComposerAreaNode | null) => {
        const isEdit = !!area;
        showDialog(
            'areaEditDialog',
            <ComposerSaveAreaDialog
                onConfirm={(area: ComposerAreaNode | null) => {
                    if (!area) return;
                    handleSaveArea(area, isEdit);
                    closeDialog('areaEditDialog');
                }}
                passedArea={area}
                meshesList={meshList}
                camerasList={camerasList}
                edit={!!area}
            />
        );
    };

    const onAreaButtonClick = (action: string) => {
        switch (action) {
            case 'add':
                handleSaveAreaButton(null);
                break;
            case 'edit':
                const selectedEditArea = treeAreasFiltered.find((x) => treeAreasSelected.includes(x.id));
                if (selectedEditArea) {
                    handleSaveAreaButton(selectedEditArea);
                }
                break;
            case 'remove':
                const selectedAreasIDs = treeAreasFiltered.filter((x) => treeAreasSelected.includes(x.id)).map((x) => x.id);
                selectedAreasIDs.forEach((id) => {
                    storeRemoveArea(id);
                });
                treeAreaRemoveNodes(selectedAreasIDs);
                break;
            case 'duplicate':
                const selectedArea = treeAreasFiltered.find((x) => treeAreasSelected.includes(x.id));
                if (selectedArea) {
                    const clonedArea = storeCloneArea(selectedArea);
                    treeAreaAddNode(clonedArea);
                }
                break;
            default:
                console.warn('Unknown action:', action);
        }
    };

    const templateNode = React.useMemo(() => treeTemplatesFiltered.sort((a, b) => a.templateID - b.templateID), [treeTemplatesFiltered]);
    const areasNode = React.useMemo(() => treeAreasFiltered, [treeAreasFiltered]);

    const nodeSelectHandler = (e: React.MouseEvent, node: ComposerTemplateNode) => {
        const ctrlKey = e.ctrlKey || e.metaKey;
        const shiftKey = e.shiftKey;
        if (shiftKey && treeTemplatesSelected.length > 0) {
            const lastSelectedId = treeTemplatesSelected[treeTemplatesSelected.length - 1];
            const lastSelectedNode = treeTemplateFindNodeById(lastSelectedId);
            if (!lastSelectedNode) return;

            const siblings = templateNode;
            const startIndex = siblings.findIndex((n) => n.id === lastSelectedNode.id);
            const endIndex = siblings.findIndex((n) => n.id === node.id);

            if (startIndex < 0 || endIndex < 0) return;

            const range = siblings.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1);
            treeTemplateSetSelectedIds(range.map((n) => n.id));
            selectTemplates(range);
            treeDesignsSetSelectedIds([]);
            return;
        }
        if (ctrlKey && treeTemplatesSelected.includes(node.id)) {
            treeTemplateSetSelectedIds(treeTemplatesSelected.filter((id) => id !== node.id));
            selectTemplates(treeTemplatesFiltered.filter((x) => treeTemplatesSelected.includes(x.id) && x.id !== node.id));
            treeDesignsSetSelectedIds([]);
            return;
        } else if (ctrlKey) {
            treeTemplateSetSelectedIds([...treeTemplatesSelected, node.id]);
            selectTemplates(treeTemplatesFiltered.filter((x) => [...treeTemplatesSelected, node.id].includes(x.id)));
            treeDesignsSetSelectedIds([]);
            return;
        }

        treeTemplateSetSelectedIds(node ? [node.id] : []);
        selectTemplates(node ? [node] : []);
        treeDesignsSetSelectedIds([treeDesignsFiltered[0]?.id]);
    };

    const onNodeToggleExpand = (node: ComposerTemplateNode) => {
        node.expanded = !node.expanded;
        treeTemplateUpdateNode(node.id, { ...node });
    };

    const nodeAreaSelectHandler = (e: React.MouseEvent, node: ComposerAreaNode) => {
        const ctrlKey = e.ctrlKey || e.metaKey;
        const shiftKey = e.shiftKey;
        if (shiftKey && treeAreasSelected.length > 0) {
            const lastSelectedId = treeAreasSelected[treeAreasSelected.length - 1];
            const lastSelectedNode = treeAreaFindNodeById(lastSelectedId);
            if (!lastSelectedNode) return;

            const siblings = treeAreasFiltered;
            const startIndex = siblings.findIndex((n) => n.id === lastSelectedNode.id);
            const endIndex = siblings.findIndex((n) => n.id === node.id);

            if (startIndex < 0 || endIndex < 0) return;

            const range = siblings.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1);
            treeAreaSetSelectedIds(range.map((n) => n.id));
            return;
        }
        if (ctrlKey && treeAreasSelected.includes(node.id)) {
            treeAreaSetSelectedIds(treeAreasSelected.filter((id) => id !== node.id));
            return;
        } else if (ctrlKey) {
            treeAreaSetSelectedIds([...treeAreasSelected, node.id]);
            return;
        }

        treeAreaSetSelectedIds(node ? [node.id] : []);
    };
    const subNodeSelectHandler = (parent: ComposerTemplateNode, node: ComposerAreaNode) => {
        treeTemplateSetSelectedIds([parent.id]);
        selectTemplates([parent]);
        treeDesignsSetSelectedIds(node ? [node.id] : []);
        selectAreas([node]);
    };

    useEffect(() => {
        if (selectedTemplate && selectedTemplate.length === 1) {
            setTimeout(() => {
                const template = selectedTemplate[0];
                treeTemplateSetSelectedIds([template.id]);
            }, 10);
        }
    }, [selectedTemplate]);

    return (
        <>
            <ComposerTemplatesContainer>
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{T._('Areas', 'Admin')}</span>
                <ComposerAreasHeader>
                    <ToolGrid tools={areaButtons} columns={4} />
                </ComposerAreasHeader>
                <ComposerTemplatesBody>
                    <ComposerTemplatesList $hasSelected={true}>
                        {treeAreasFiltered.length < 1 && <ComposerTemplatesEmptyState label={T._('No Areas, create one to get started', 'Admin')} />}
                        <ComposerTemplatesSelector
                            selected={treeAreasSelected.length > 0}
                            onClick={() => {
                                if (treeAreasSelected.length > 0) {
                                    treeAreaSetSelectedIds([]);
                                } else {
                                    treeAreaSetSelectedIds(treeAreasFiltered.map((attr) => attr.id));
                                }
                            }}
                        />
                        {areasNode.map((node) => (
                            <ComposerTemplateTreeElement
                                key={node.id}
                                icon={<></>}
                                name={node.name}
                                expanded={false}
                                selected={treeAreasSelected.includes(node.id)}
                                onSelect={(e) => nodeAreaSelectHandler(e, node)}
                                onDoubleClick={(e) => handleSaveAreaButton(node)}
                            />
                        ))}
                    </ComposerTemplatesList>
                </ComposerTemplatesBody>
            </ComposerTemplatesContainer>
            <ComposerTemplatesContainer>
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{T._('Templates', 'Admin')}</span>
                <ComposerTemplatesHeader>
                    <ToolGrid tools={templateButtons} columns={4} />
                    {/* <ComposerButtonGroup elements={buttons} onElementClick={(key) => onAttributeButtonClick(key)} /> */}
                </ComposerTemplatesHeader>
                <ComposerTemplatesBody>
                    <ComposerTemplatesList $hasSelected={true}>
                        {treeAreasFiltered.length < 1 && <ComposerTemplatesEmptyState label={T._('No Areas, create one to get started', 'Admin')} />}

                        <ComposerTemplatesCollapseSelector
                            onExpand={() => {
                                templateNode.forEach((node) => {
                                    node.expanded = true;
                                    treeTemplateUpdateNode(node.id, { ...node });
                                });
                            }}
                            onCollapse={() => {
                                templateNode.forEach((node) => {
                                    node.expanded = false;
                                    treeTemplateUpdateNode(node.id, { ...node });
                                });
                            }}
                        />
                        {treeAreasFiltered.length > 0 &&
                            templateNode.map((node) => (
                                <ComposerTemplateTreeElement
                                    key={node.id}
                                    icon={<></>}
                                    name={node.name}
                                    expanded={node.expanded}
                                    selected={treeTemplatesSelected.includes(node.id)}
                                    onToggleExpand={() => onNodeToggleExpand(node)}
                                    onSelect={(e) => nodeSelectHandler(e, node)}
                                >
                                    {areasNode.map((sub) => (
                                        <ComposerTemplateSubTreeElement
                                            key={sub.id}
                                            icon={<></>}
                                            name={sub.name}
                                            selected={treeDesignsSelected.includes(sub.id) && treeTemplatesSelected.includes(node.id)}
                                            onSelect={() => subNodeSelectHandler(node, sub)}
                                        />
                                    ))}
                                </ComposerTemplateTreeElement>
                            ))}
                    </ComposerTemplatesList>
                </ComposerTemplatesBody>
            </ComposerTemplatesContainer>
        </>
    );
};

export default ComposerTemplates;
